import React from 'react';
import { 
  Snackbar, 
  Alert, 
  Slide,
  Grow
} from '@mui/material';
import { 
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

const StyledAlert = styled(Alert)(({ theme, severity }) => ({
  borderRadius: theme.spacing(1),
  fontWeight: 600,
  fontSize: '0.9rem',
  minWidth: 350,
  maxWidth: 500,
  boxShadow: theme.shadows[8],
  '& .MuiAlert-icon': {
    fontSize: '1.5rem',
  },
  '& .MuiAlert-message': {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 0',
  },
  // Enhanced styling based on severity
  ...(severity === 'success' && {
    backgroundColor: '#4caf50',
    color: 'white',
    '& .MuiAlert-icon': {
      color: 'white',
    },
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
  }),
  ...(severity === 'error' && {
    backgroundColor: '#f44336',
    color: 'white',
    '& .MuiAlert-icon': {
      color: 'white',
    },
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
  }),
  ...(severity === 'warning' && {
    backgroundColor: '#ff9800',
    color: 'white',
    '& .MuiAlert-icon': {
      color: 'white',
    },
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
  }),
  ...(severity === 'info' && {
    backgroundColor: '#2196f3',
    color: 'white',
    '& .MuiAlert-icon': {
      color: 'white',
    },
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
  }),
}));

const SlideTransition = (props) => {
  return <Slide {...props} direction="left" />;
};

const GrowTransition = (props) => {
  return <Grow {...props} />;
};

const SnackbarNotification = ({
  open,
  message,
  severity = 'success',
  autoHideDuration = 6000,
  onClose,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  transition = 'slide', // 'slide' or 'grow'
  showIcon = true,
  ...other
}) => {
  const getIcon = (severity) => {
    switch (severity) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return null;
    }
  };

  const TransitionComponent = transition === 'grow' ? GrowTransition : SlideTransition;

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      TransitionComponent={TransitionComponent}
      sx={{ 
        zIndex: 2100, // Higher than modals
      }}
      {...other}
    >
      <StyledAlert
        onClose={onClose}
        severity={severity}
        variant="filled"
        icon={showIcon ? getIcon(severity) : false}
        sx={{
          '& .MuiAlert-action': {
            color: 'inherit',
            '& .MuiSvgIcon-root': {
              color: 'inherit',
            },
          },
        }}
      >
        {message}
      </StyledAlert>
    </Snackbar>
  );
};

SnackbarNotification.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  autoHideDuration: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  transition: PropTypes.oneOf(['slide', 'grow']),
  showIcon: PropTypes.bool,
};

export default SnackbarNotification; 